<script lang="ts" setup>
import { EventItem } from '@/models/event-item.model';
import { Initiative } from '@/models/initiative.model';
import { nameRules, Organization } from '@/models/organization.model';
import { User } from '@/models/user.model';
import { useEvents, Event } from '@/services/events.composable';
import { FilterData } from '@/services/users.service';
import { Actions, Mutations, ShiftCMSState } from '@/store';
import { ContentStoreActions, ContentStoreState } from '@/stores/content.store';
import { EventsActions, EventsState } from '@/stores/events.store';

import { UsersActions, UsersState } from '@/stores/users.store';
import { NEW_ID } from '@/util/constants';
import { computed, onMounted, PropType, ref } from 'vue';
import { useStore } from 'vuex';

const store = useStore<
  ShiftCMSState & { us: UsersState; cns: ContentStoreState; ev: EventsState }
>();

const events = useEvents();

const users = computed<User[]>(() => store.state.us.users);

const userOptions = computed(() =>
  users.value
    .filter((u) => !!u.firstName && !!u.lastName)
    .filter(
      (u) =>
        !(organization.value.users || []).map((u2) => u2.id).includes(u.id),
    )
    .map((v) => ({
      title: `${v.firstName} ${v.lastName} (id: ${v.id})`,
      value: v,
    })),
);
const initiatives = computed<Initiative[]>(() => store.state.cns.initiatives);

const initiativeOptions = computed(() =>
  initiatives.value
    .filter((u) => !!u.title)
    .filter(
      (u) => !organization.value.initiatives.map((u2) => u2.id).includes(u.id),
    )
    .map((v) => ({
      title: `${v.title} (id: ${v.id})`,
      value: v,
    })),
);

const eventItems = computed<EventItem[]>(() => store.state.ev.items);

const eventOptions = computed(() =>
  eventItems.value
    .filter((u) => !!u.title)
    .filter(
      (u) => !organization.value.initiatives.map((u2) => u2.id).includes(u.id),
    )
    .map((v) => ({
      title: `${v.title} (id: ${v.id})`,
      value: v,
    })),
);

const filterData = ref<FilterData>({
  search: '',
  level: null,
  page: 1,
  count: 25,
  sort: 'id',
  direction: 'asc',
});

function updateUsers(value: string) {
  filterData.value.search = value;
  store.dispatch(UsersActions.GetUserList, filterData.value);
}

const userToAdd = ref<User | null>(null);

function addUser() {
  if (userToAdd.value) {
    organization.value = {
      ...organization.value,
      users: [...organization.value.users, userToAdd.value],
    };

    userToAdd.value = null;
  }
}

const filterData2 = ref<FilterData>({
  search: '',
  level: null,
  page: 1,
  count: 25,
  sort: 'id',
  direction: 'asc',
});

function updateInitiatives(value: string) {
  filterData2.value.search = value;
  store.dispatch(
    ContentStoreActions.GetInitiativesPaginated,
    filterData2.value,
  );
}

const initiativeToAdd = ref<Initiative | null>(null);

function addInitiative() {
  if (initiativeToAdd.value) {
    organization.value = {
      ...organization.value,
      initiatives: [...organization.value.initiatives, initiativeToAdd.value],
    };

    initiativeToAdd.value = null;
  }
}

const filterData3 = ref<FilterData>({
  search: '',
  level: null,
  page: 1,
  count: 25,
  sort: 'id',
  direction: 'asc',
});

function updateEvents(value: string) {
  filterData3.value.search = value;
  store.dispatch(EventsActions.GetEventItemsPaginated, filterData3.value);
}

const eventToAdd = ref<EventItem | null>(null);

function addEvent() {
  if (eventToAdd.value) {
    organization.value = {
      ...organization.value,
      events: [...organization.value.events, eventToAdd.value],
    };

    eventToAdd.value = null;
  }
}
function removeUser(user: User) {
  organization.value.users = [
    ...organization.value.users.filter((v) => v.id !== user.id),
  ];
  return null;
}

function removeInitiative(initiative: Initiative) {
  organization.value.initiatives = [
    ...organization.value.initiatives.filter((v) => v.id !== initiative.id),
  ];
  return null;
}

function removeEvent(event: EventItem) {
  organization.value.events = [
    ...organization.value.events.filter((v) => v.id !== event.id),
  ];
  return null;
}

const props = defineProps({
  data: {
    type: Object as PropType<Organization>,
    required: true,
  },
});

const valid = ref<boolean>(false);

const organization = ref<Organization>({
  ...props.data,
});

const action = computed<string>(() =>
  organization.value.id === NEW_ID ? 'Add' : 'Save',
);

const saveOrganization = async () => {
  let success: boolean = false;
  if (valid.value) {
    if (organization.value.id === NEW_ID) {
      success = await store.dispatch(
        UsersActions.AddOrganization,
        organization.value,
      );
    } else {
      success = await store.dispatch(
        UsersActions.UpdateOrganization,
        organization.value,
      );
    }
  }
  if (success) {
    events.emit(Event.Saved, true);
    store.dispatch(Actions.ShowNotification, {
      message: 'Saved Organization',
      type: 'success',
    });
    store.commit(Mutations.HideDrawer);
    store.dispatch(UsersActions.GetOrganizationsList);
  } else {
    store.dispatch(Actions.ShowNotification, {
      message: 'Failed to save Organization',
      type: 'error',
    });
  }
};

onMounted(() => {
  if (props.data) {
    Object.assign(organization.value, {
      ...props.data,
    });
  }
});

const deleteOrganization = async () => {
  if (
    organization.value &&
    organization.value.id !== NEW_ID &&
    confirm('Are you absolutely sure you want to delete this "Organization"?')
  ) {
    store.dispatch(UsersActions.DeleteOrganization, organization.value);

    store.dispatch(UsersActions.GetOrganizationsList);
    store.commit(Mutations.HideDrawer);
  }
};
</script>
<template>
  <v-form v-model="valid" @submit.prevent="saveOrganization">
    <v-container>
      <h2>{{ $t(action) }} an organization</h2>
      <v-row>
        <v-col class="mx-auto">
          <v-text-field
            v-model="organization.name"
            label="Name"
            :rules="nameRules"
          ></v-text-field>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="mx-auto">
          <v-text-field
            v-model="organization.webToken"
            label="Web Token"
            type="password"
            disabled
          ></v-text-field>
        </v-col>
      </v-row>

      <v-row>
        <v-col class="mx-auto">
          <v-autocomplete
            v-model="userToAdd"
            :items="userOptions"
            @update:search="updateUsers"
            label="Add users"
          ></v-autocomplete>
        </v-col>
        <v-col cols="3">
          <v-btn @click="addUser" :disabled="!userToAdd">Add user</v-btn>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-container>
            <h2>Users in this organization</h2>
            <ul style="margin: 20px">
              <li v-for="user in organization.users" :key="user.id">
                <div style="width: 300px; display: inline-block">
                  {{ user.firstName }} {{ user.lastName }}
                </div>
                <v-btn
                  class="mt-2"
                  type="button"
                  color="warning"
                  density="compact"
                  icon="mdi-delete-outline"
                  @click="removeUser(user)"
                ></v-btn>
              </li>
            </ul>
          </v-container>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="mx-auto">
          <v-autocomplete
            v-model="initiativeToAdd"
            :items="initiativeOptions"
            @update:search="updateInitiatives"
            label="Add places"
          ></v-autocomplete>
        </v-col>
        <v-col cols="3">
          <v-btn @click="addInitiative" :disabled="!initiativeToAdd"
            >Add place</v-btn
          >
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-container>
            <h2>Places in this organization</h2>
            <ul style="margin: 20px">
              <li
                v-for="initiative in organization.initiatives"
                :key="initiative.id"
              >
                <div style="width: 300px; display: inline-block">
                  {{ initiative.title }}
                </div>

                <v-btn
                  class="mt-2"
                  type="button"
                  color="warning"
                  density="compact"
                  icon="mdi-delete-outline"
                  @click="removeInitiative(initiative)"
                ></v-btn>
              </li>
            </ul>
          </v-container>
        </v-col>
      </v-row>
      <v-row>
        <v-col class="mx-auto">
          <v-autocomplete
            v-model="eventToAdd"
            :items="eventOptions"
            @update:search="updateEvents"
            label="Add events"
          ></v-autocomplete>
        </v-col>
        <v-col cols="3">
          <v-btn @click="addEvent" :disabled="!eventToAdd">Add event</v-btn>
        </v-col>
      </v-row>
      <v-row>
        <v-col>
          <v-container>
            <h2>Events in this organization</h2>
            <ul style="margin: 20px">
              <li v-for="event in organization.events" :key="event.id">
                <div style="width: 300px; display: inline-block">
                  {{ event.title }}
                </div>

                <v-btn
                  class="mt-2"
                  type="button"
                  icon="mdi-delete-outline"
                  color="warning"
                  density="compact"
                  @click="removeEvent(event)"
                ></v-btn>
              </li>
            </ul>
          </v-container>
        </v-col>
      </v-row>
      <v-row>
        <v-col col="2"></v-col>
        <v-col>
          <v-btn
            v-if="organization.id !== NEW_ID"
            class="mt-2"
            type="button"
            color="warning"
            density="compact"
            @click="deleteOrganization"
            block
            >Delete</v-btn
          >
        </v-col>
        <v-col>
          <v-btn class="mt-2" type="reset" block>Reset</v-btn>
        </v-col>
        <v-col>
          <v-btn class="mt-2" type="submit" color="success" block>{{
            action
          }}</v-btn>
        </v-col>
        <v-col col="2"></v-col>
      </v-row>
    </v-container>
  </v-form>
</template>
<style scoped>
pre {
  font-family: sans-serif;
  font-size: 12px;
  background-color: rgb(255, 230, 171);
  display: block;
  height: 100%;
  padding: 8px;
}
</style>
